import { type ChangeEvent, type FC, type ReactNode, useState } from "react";
import { type LinearCstic, type LinearCsticValue, type LinearSubstep } from "~/features/configurator/Linear/models";
import { useDispatch, useSelector } from "react-redux";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import {
  getCsticMap,
  selectMainKmatCstics,
  selectSchmieranschlussTabConfig,
  shouldEnableConfigureCarriageValuesLinear,
} from "~/features/configurator/Linear/redux/selectors";
import {
  CARRIAGE_DESIGN_SUBKMAT_KWVE,
  CARRIAGE_DESIGN_SUBKMAT_RWU,
  CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME,
  CSTIC_CARRIAGE_LEFT_NAME,
  CSTIC_CARRIAGE_LOCATING_FACE,
  CSTIC_CARRIAGE_MIDDLE_NAME,
  CSTIC_CARRIAGE_RIGHT_NAME,
  CSTIC_DESIGN_TYPE_SUBLEVEL_NAME,
  CSTIC_GUIDEWAY_AMOUNT_NAME,
  CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME,
  CSTIC_SCHMIERANSCHLUSS_NAME,
  CSTIC_SUPPORT_DICHTUNGS_NAME,
  CSTIC_SUPPORT_KIT_NAME,
  CSTIC_VHOE,
  CSTIC_VHOE_STANDARD_VALUE,
  CSTIC_WAGENBAUFORM_NAME,
  GET_CARRIAGE_AMOUNT_CSTIC_NAME,
  GET_CARRIAGE_DETAILS_IMAGE_URL,
  KITS_LOWER_THRESHOLD,
  KITS_UPPER_THRESHOLD,
  MOUNTED_LEFT,
  MOUNTED_RIGHT,
  UNMOUNTED_LEFT,
  UNMOUNTED_RIGHT,
} from "~/features/configurator/Linear/constants";
import {
  LinearSection,
  LinearSectionTitle,
} from "~/features/configurator/Linear/components/Substeps/GuidewaySetStep/GuidewaySetStep";
import { defaultMemoize } from "reselect";
import { type CustomDispatch } from "~/app/store/types";
import { type RootState } from "~/app/store";
import { editLinearDetails, resetLinearDetails, updateCsticValues } from "~/features/configurator/Linear/redux/actions";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { Col, Row } from "~/common/components/Layout";
import PopupSelectionCstic from "~/features/configurator/Linear/components/SingleCstics/PopupSelectionCstic";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { LCTileImage } from "~/features/configurator/Linear/components/LinearTile";
import { Card } from "~/features/configurator/Linear/components/Card";
import { Button, type SelectChangeEvent, Stack, Tab, TabContext, TabList, TabPanel } from "~/common/mui";
import { formatCsticsToSet } from "~/features/configurator/Linear/utils";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";

import { LinearTranslation } from "~/features/configurator/common/components/LinearTranslation";
import { RESET_STANDARD } from "~/features/configurator/common/advancedTranslationUtils";
import { type DropdownEntry } from "~/common/types";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import classNames from "classnames";
import * as CarriageCstic from "~/features/configurator/Carriage/v2/configuration/CarriageCstics";
import { KmatType } from "~/features/configurator/Linear/kmatType";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const CarriageDetailsSubstep: FC<Props> = ({ def, ...props }) => {
  const cstics = useSelector(selectMainKmatCstics);
  const tabList = prepareTabData(cstics, def);

  const [activeTabIndex, setActiveTab] = useState<string>("0");

  const { t } = useTranslation();
  if (tabList.length === 0) {
    return null;
  }

  const getTabLabel = (guidewayIndex: number) =>
    t("page.linear.carriageDetails.GuidewayTabTitle", { index: guidewayIndex + 1 });

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      {def.footerInfo.length > 0 && (
        <div className="mb-3">
          {def.footerInfo.map((info) => (
            <Alert key={info} className="mb-2" severity="info">
              <LanguageAwareExternalContent content={info} className={"scha__lc__external-label"} />
            </Alert>
          ))}
        </div>
      )}

      <div className={"w-100 mb-30"}>
        <TabContext value={activeTabIndex}>
          <TabList onChange={(e, val) => setActiveTab(val)}>
            {tabList.map(({ guidewayIndex }) => (
              <Tab key={guidewayIndex} value={"" + guidewayIndex} label={getTabLabel(guidewayIndex)} />
            ))}
          </TabList>
          {tabList.map((props) => (
            <SingleGuidewayTab key={props.guidewayIndex} {...props} def={def} />
          ))}
        </TabContext>
      </div>
    </SubstepFrame>
  );
};
export default CarriageDetailsSubstep;

const prepareTabData = (defaultKmatCstics: Dict<LinearCstic>, def: LinearSubstep): SingleGuidewayTabProps[] => {
  const guidewayCstic = defaultKmatCstics[CSTIC_GUIDEWAY_AMOUNT_NAME];
  if (!guidewayCstic) {
    return [];
  }
  const gAmount = +guidewayCstic.value!;

  let startingCarriageIndex = 0;

  return new Array(gAmount).fill(1).map((_, guidewayIndex) => {
    const carriageAmountCstic = defaultKmatCstics[GET_CARRIAGE_AMOUNT_CSTIC_NAME(guidewayIndex + 1)];
    const carriageAmount = carriageAmountCstic ? +carriageAmountCstic.value! : 0;

    const props: SingleGuidewayTabProps = {
      def,
      guidewayIndex,
      startingCarriageIndex,
      carriageAmount,
      guidewayAmount: gAmount,
    };

    startingCarriageIndex += carriageAmount;

    return props;
  });
};

interface SingleGuidewayTabProps {
  def: LinearSubstep;
  guidewayIndex: number;
  startingCarriageIndex: number;
  carriageAmount: number;
  guidewayAmount: number;
}

const SingleGuidewayTab: FC<SingleGuidewayTabProps> = ({
  guidewayIndex,
  startingCarriageIndex,
  carriageAmount,
  guidewayAmount,
  def,
}) => {
  const tabsList = new Array(carriageAmount).fill(startingCarriageIndex).map((c, i) => c + i);
  const enableConfigureCarriageValues = useSelector(shouldEnableConfigureCarriageValuesLinear);

  return (
    <TabPanel value={"" + guidewayIndex} keepMounted>
      <Row className={"mt-30"}>
        {tabsList.map((carriageIndex, indexInGuideway) => (
          <Col size={3} key={indexInGuideway}>
            <SingleTile
              def={def}
              carriageIndex={carriageIndex}
              index={indexInGuideway}
              guidewayIndex={guidewayIndex}
              guidewayAmount={guidewayAmount}
              carriageAmount={carriageAmount}
              disableEditCarriage={enableConfigureCarriageValues ? !enableConfigureCarriageValues[carriageIndex] : true}
            />
          </Col>
        ))}
      </Row>
    </TabPanel>
  );
};

interface TileProps {
  carriageIndex: number; // Carriage index in redux - 0 based
  guidewayIndex: number; // Guideway index in redux - 0 based
  index: number; // Index of carriage in guideway - 0 based
  guidewayAmount: number;
  carriageAmount: number;
  disableEditCarriage: boolean;
  def: LinearSubstep;
}

const SingleTile: FC<TileProps> = ({
  def,
  carriageIndex,
  index,
  guidewayIndex,
  guidewayAmount,
  carriageAmount,
  disableEditCarriage,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cstics = useSelector(getCsticMap(KmatType.SubCarriage, carriageIndex));
  const tabConfig = useSelector(selectSchmieranschlussTabConfig);

  const leftCstic = cstics[CSTIC_CARRIAGE_LEFT_NAME];
  const leftCsticDDValues = makeDropdownElements(leftCstic, carriageIndex, dispatch, true);
  const leftCsticSelectedEl = leftCsticDDValues.filter((el) => el.value === leftCstic.value)[0];

  const middleCstic = cstics[CSTIC_CARRIAGE_MIDDLE_NAME];
  const middleCsticDDValues = makeDropdownElements(middleCstic, carriageIndex, dispatch, true);
  const middleCsticSelectedEl = middleCsticDDValues.filter((el) => el.value === middleCstic.value)[0];

  const rightCstic = cstics[CSTIC_CARRIAGE_RIGHT_NAME];
  const rightCsticDDValues = makeDropdownElements(rightCstic, carriageIndex, dispatch, true);
  const rightCsticSelectedEl = rightCsticDDValues.filter((el) => el.value === rightCstic.value)[0];

  const schmierCstic = cstics[CSTIC_SCHMIERANSCHLUSS_NAME];
  const designTypeCstic = cstics[CSTIC_DESIGN_TYPE_SUBLEVEL_NAME];
  let leftKitsNumericValue = 0;
  let rightKitsNumericValue = 0;
  if (leftCstic && leftCstic.value) {
    leftKitsNumericValue = isNaN(Number(leftCstic.value)) ? 0 : Number(leftCstic.value);
  }
  if (rightCstic && rightCstic.value) {
    rightKitsNumericValue = isNaN(Number(rightCstic.value)) ? 0 : Number(rightCstic.value);
  }
  const schmierCsticDDValues = makeDropdownElementsForLubricationAdapter(
    schmierCstic,
    designTypeCstic,
    leftKitsNumericValue,
    rightKitsNumericValue,
    carriageIndex,
    dispatch,
  );
  const schmierCsticSelectedEl = schmierCsticDDValues.filter((el) => el.value === schmierCstic.value)[0];

  const schmierDegreeCstic = cstics[CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME];
  const schmierDegreeCsticDDValues = makeDropdownElements(schmierDegreeCstic, carriageIndex, dispatch, false);
  const schmierDegreeCsticSelectedEl = schmierDegreeCsticDDValues.filter(
    (el) => el.value === schmierDegreeCstic.value,
  )[0];

  const schmierPositionCstic = cstics[CarriageCstic.LubricationConnectorPosition];
  const schmierPositionCsticDDValues = makeDropdownElements(schmierPositionCstic, carriageIndex, dispatch, false);
  const schmierPositionCsticSelectedEl = schmierPositionCsticDDValues.filter(
    (el) => el.value === schmierPositionCstic.value,
  )[0];

  const schmierAuswahlCstic = cstics[CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME];

  const locatingFaceCstic = cstics[CSTIC_CARRIAGE_LOCATING_FACE];

  const dichtungSupportCstic = cstics[CSTIC_SUPPORT_DICHTUNGS_NAME];
  const kitSupportCstic = cstics[CSTIC_SUPPORT_KIT_NAME];

  const title = t(`page.linear.carriageDetails.title.${locatingFaceCstic.value == "U" ? "below" : "above"}`, {
    carriageIndex: index + 1,
    guidewayIndex: guidewayIndex + 1,
  });

  const carriageImageUrl = GET_CARRIAGE_DETAILS_IMAGE_URL({
    carriageIndex,
    carriageAmount,
    guidewayAmount,
  });

  const isLeftsKitsValid = leftCsticSelectedEl && leftCsticSelectedEl.value != undefined;
  const isMiddleKitsValid = middleCsticSelectedEl && middleCsticSelectedEl.value != undefined;
  const isRightKitsValid = rightCsticSelectedEl && rightCsticSelectedEl.value != undefined;
  const isLubricationConnectorValid = schmierAuswahlCstic && schmierAuswahlCstic.value != undefined;
  const isSchmierPositionValid = schmierPositionCstic && schmierPositionCstic.value != undefined;
  const isSchmierDegreeValid = schmierDegreeCstic && schmierDegreeCstic.value != undefined;

  return (
    <Card disableHover={true}>
      <div className={"px-gutter-half"}>
        <LCTileImage className={"w-100 mb-3 scha__lc__c-details__image"} imageUrl={carriageImageUrl} />

        <div className={"px-gutter-half"}>
          <LinearSectionTitle className={"mt-0 mb-30 f-pre-line"}>{title}</LinearSectionTitle>

          <PopupSelectionCstic
            preClick={() =>
              dispatch(
                updateCsticValues(
                  [
                    {
                      type: KmatType.Main,
                      value: CSTIC_VHOE_STANDARD_VALUE,
                      key: CSTIC_VHOE,
                    },
                  ],
                  SubstepType.CarriageDetails,
                ),
              )
            }
            className={"mb-30"}
            carriageIndex={carriageIndex}
            cstic={cstics[CSTIC_WAGENBAUFORM_NAME]}
            substep={SubstepType.CarriageDetails}
            selectionInfo={cstics[CSTIC_WAGENBAUFORM_NAME].info}
          />
          <LinearSectionTitle
            className={"mt-0 mb-3 f-bold"}
            info={def.extraTexts["sealingAndLubricationSectionInfo"]}
            addModalParam={true}
          >
            <LanguageAwareExternalContent
              content={def.extraTexts["sealingAndLubricationSectionName"]}
              className={"scha__lc__external-label"}
            />
          </LinearSectionTitle>

          <LinearSection label={kitSupportCstic.label} info={kitSupportCstic.info}>
            <div className={"scha__lc__kit"}>
              {leftCstic && (
                <KitInfoWrapper cstic={leftCstic} addModalParam={true}>
                  <SimpleSelect
                    label={
                      <LanguageAwareExternalContent content={leftCstic.label} className={"scha__lc__external-label"} />
                    }
                    values={leftCsticDDValues}
                    value={leftCsticSelectedEl ? leftCsticSelectedEl.value : null}
                    onChange={DROPDOWN_ON_CLICK(leftCsticDDValues)}
                    showFilter={true}
                    error={!isLeftsKitsValid}
                    fullWidth={true}
                    disabled={disableEditCarriage}
                  />
                </KitInfoWrapper>
              )}

              {middleCstic && (
                <KitInfoWrapper cstic={middleCstic} addModalParam={true}>
                  <SimpleSelect
                    label={
                      <LanguageAwareExternalContent
                        content={middleCstic.label}
                        className={"scha__lc__external-label"}
                      />
                    }
                    values={middleCsticDDValues}
                    value={middleCsticSelectedEl ? middleCsticSelectedEl.value : null}
                    onChange={DROPDOWN_ON_CLICK(middleCsticDDValues)}
                    showFilter={true}
                    error={!isMiddleKitsValid}
                    fullWidth={true}
                    disabled={disableEditCarriage}
                  />
                </KitInfoWrapper>
              )}

              {rightCstic && (
                <KitInfoWrapper cstic={rightCstic} addModalParam={true}>
                  <SimpleSelect
                    label={
                      <LanguageAwareExternalContent content={rightCstic.label} className={"scha__lc__external-label"} />
                    }
                    values={rightCsticDDValues}
                    value={rightCsticSelectedEl ? rightCsticSelectedEl.value : null}
                    onChange={DROPDOWN_ON_CLICK(rightCsticDDValues)}
                    showFilter={true}
                    error={!isRightKitsValid}
                    fullWidth={true}
                    disabled={disableEditCarriage}
                  />
                </KitInfoWrapper>
              )}
            </div>
          </LinearSection>

          {schmierCstic && (
            <LinearSection
              info={def.extraTexts["lubricationConnectorSectionInfo"]}
              label={
                <LanguageAwareExternalContent
                  content={def.extraTexts["lubricationConnectorSectionName"]}
                  className={"scha__lc__external-label"}
                />
              }
            >
              <SimpleSelect
                values={schmierCsticDDValues}
                value={schmierCsticSelectedEl ? schmierCsticSelectedEl.value : null}
                onChange={DROPDOWN_ON_CLICK(schmierCsticDDValues)}
                fullWidth={true}
                disabled={disableEditCarriage}
              />
            </LinearSection>
          )}

          {schmierPositionCstic && (
            <LinearSection
              info={schmierPositionCstic.info}
              label={
                <LanguageAwareExternalContent
                  content={schmierPositionCstic.label}
                  className={"scha__lc__external-label"}
                />
              }
            >
              <SimpleSelect
                values={schmierPositionCsticDDValues}
                value={schmierPositionCsticSelectedEl ? schmierPositionCsticSelectedEl.value : null}
                onChange={DROPDOWN_ON_CLICK(schmierPositionCsticDDValues)}
                error={!isSchmierPositionValid}
                fullWidth={true}
                disabled={disableEditCarriage}
              />
            </LinearSection>
          )}

          {schmierAuswahlCstic && schmierAuswahlCstic.values.length > 0 && (
            <PopupSelectionCstic
              className={"mb-30"}
              carriageIndex={carriageIndex}
              cstic={cstics[CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME]}
              substep={SubstepType.CarriageDetails}
              tabConfig={tabConfig}
              hasError={!isLubricationConnectorValid}
              disabled={disableEditCarriage}
            />
          )}

          {schmierDegreeCstic && (
            <LinearSection
              info={schmierDegreeCstic.info}
              label={
                <LanguageAwareExternalContent
                  content={schmierDegreeCstic.label}
                  className={"scha__lc__external-label"}
                />
              }
            >
              <SimpleSelect
                values={schmierDegreeCsticDDValues}
                value={schmierDegreeCsticSelectedEl ? schmierDegreeCsticSelectedEl.value : null}
                onChange={DROPDOWN_ON_CLICK(schmierDegreeCsticDDValues)}
                error={!isSchmierDegreeValid}
                fullWidth={true}
                disabled={disableEditCarriage}
              />
            </LinearSection>
          )}
        </div>

        <Stack gap={2} direction={"row"} className={"pb-3"}>
          <Button
            color={"secondary"}
            fullWidth={true}
            onClick={() => dispatch(resetLinearDetails(carriageIndex, false))}
          >
            <LinearTranslation translationKey={RESET_STANDARD} />
          </Button>
          <Button
            color={"primary"}
            fullWidth={true}
            onClick={() => dispatch(editLinearDetails(carriageIndex, SubstepType.CarriageDetails))}
          >
            <LanguageAwareExternalContent content={def.extraTexts["edit"]} className={"scha__lc__external-label"} />
          </Button>
        </Stack>
      </div>
    </Card>
  );
};

type EnhancedDropdownEntry = DropdownEntry<string> & { onClick: () => void };

const makeDropdownElements = defaultMemoize(
  (
    cstic: LinearCstic | undefined,
    kmatIndex: number,
    dispatch: CustomDispatch<RootState>,
    isFilterable: boolean,
  ): EnhancedDropdownEntry[] => {
    if (!cstic) {
      return [];
    }
    return cstic.values.map((csticVal) => ({
      label: isDebugLanguageEnabled() ? (
        formatCsticsToSet({ [cstic.name]: csticVal.value })
      ) : (
        <LanguageAwareExternalContent content={csticVal.label} className={"scha__lc__external-label"} />
      ),
      value: csticVal.key,
      onClick: () =>
        dispatch(
          updateCsticValues(
            [
              {
                type: KmatType.SubCarriage,
                value: csticVal.value,
                key: cstic.key,
                index: kmatIndex,
              },
            ],
            SubstepType.CarriageDetails,
          ),
        ),
      stringLabel: isFilterable ? csticVal.label : undefined,
      defaultValue: csticVal.standard,
    }));
  },
);

const makeDropdownElementsForLubricationAdapter = defaultMemoize(
  (
    cstic: LinearCstic | undefined,
    designTypeCstic: LinearCstic | undefined,
    leftKitsNumericValue: number,
    rightKitsNumericValue: number,
    kmatIndex: number,
    dispatch: CustomDispatch<RootState>,
  ): EnhancedDropdownEntry[] => {
    if (!cstic) {
      return [];
    }
    if (!cstic.values) {
      return [];
    }

    return cstic.values
      .filter((csticVal) => {
        return isValueAllowed(csticVal, designTypeCstic, leftKitsNumericValue, rightKitsNumericValue);
      })
      .map((csticVal) => ({
        label: isDebugLanguageEnabled() ? (
          formatCsticsToSet({ [cstic.name]: csticVal.value })
        ) : (
          <LanguageAwareExternalContent content={csticVal.label} className={"scha__lc__external-label"} />
        ),
        value: csticVal.key,
        onClick: () =>
          dispatch(
            updateCsticValues(
              [
                {
                  type: KmatType.SubCarriage,
                  value: csticVal.value,
                  key: cstic.key,
                  index: kmatIndex,
                },
              ],
              SubstepType.CarriageDetails,
            ),
          ),
        defaultValue: csticVal.standard,
      }));
  },
);

const DROPDOWN_ON_CLICK = (allValues: EnhancedDropdownEntry[]) => (e: ChangeEvent<HTMLInputElement>) => {
  const val = e.target.value as string;
  if (val) {
    allValues.find((item) => item.value === val)?.onClick();
  }
};
// const DROPDOWN_ON_CLICK2 = (allValues: EnhancedDropdownEntry[]) => (e: SelectChangeEvent<unknown>) => {
//   const val = e.target.value as string;
//   if (val) {
//     allValues.find((item) => item.value === val)?.onClick();
//   }
// };

const isValueAllowed = (
  csticValue: LinearCsticValue | null | undefined,
  designTypeCstic: LinearCstic | undefined,
  leftKitsNumericValue: number,
  rightKitsNumericValue: number,
) => {
  if (!csticValue || !csticValue.value) {
    return false;
  }

  if (designTypeCstic) {
    if (designTypeCstic.value === CARRIAGE_DESIGN_SUBKMAT_RWU) {
      if (leftKitsNumericValue >= KITS_LOWER_THRESHOLD && leftKitsNumericValue <= KITS_UPPER_THRESHOLD) {
        return csticValue.value !== MOUNTED_LEFT && csticValue.value !== UNMOUNTED_LEFT;
      }
      if (rightKitsNumericValue >= KITS_LOWER_THRESHOLD && rightKitsNumericValue <= KITS_UPPER_THRESHOLD) {
        return csticValue.value !== MOUNTED_RIGHT && csticValue.value !== UNMOUNTED_RIGHT;
      }
    } else if (designTypeCstic.name === CARRIAGE_DESIGN_SUBKMAT_KWVE) {
      if (leftKitsNumericValue == KITS_LOWER_THRESHOLD) {
        return csticValue.value !== MOUNTED_LEFT && csticValue.value !== UNMOUNTED_LEFT;
      }
      if (rightKitsNumericValue == KITS_LOWER_THRESHOLD) {
        return csticValue.value !== MOUNTED_RIGHT && csticValue.value !== UNMOUNTED_RIGHT;
      }
    }
  }

  return true;
};

export const KitInfoWrapper = ({
  cstic,
  children,
  addModalParam,
  hidden,
}: {
  cstic: LinearCstic;
  children: ReactNode;
  addModalParam?: boolean;
  hidden?: boolean;
}) => (
  <div className={"p-relative"} hidden={hidden}>
    {children}
    {cstic.info && (
      <LinearInfo
        className={"scha__lc__kit-info-icon"}
        info={cstic.info}
        popupClassName={classNames("scha__lc__external-content--shaft", addModalParam && "scha__lc__kits-table")}
      />
    )}
  </div>
);
